import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-20px);
    }
`;

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const LoadingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    padding: 20px;
    text-align: center;
    background: #ffffff;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(45deg, rgba(127, 0, 255, 0.03) 0%, rgba(127, 0, 255, 0.01) 100%);
        z-index: 0;
    }

    * {
        position: relative;
        z-index: 1;
    }
`;

const Logo = styled.div`
    width: 120px;
    height: 120px;
    margin-bottom: 2rem;
    animation: ${fadeIn} 0.8s ease-out;
`;

const LoadingSpinner = styled.div`
    width: 50px;
    height: 50px;
    border: 4px solid rgba(127, 0, 255, 0.1);
    border-radius: 50%;
    border-top-color: #7f00ff;
    animation: ${spin} 1s linear infinite;
    margin-bottom: 2rem;
`;

const TypingText = styled.div`
    margin-top: 1rem;
    font-size: 1.25rem;
    color: #2d3748;
    animation: ${fadeIn} 1s ease-in-out;
    display: flex;
    flex-direction: column;
    align-items: center;
    opacity: 0.9;
    overflow: hidden;
    max-width: 600px;
    line-height: 1.6;
    font-weight: 500;
`;

const FeatureList = styled.ul`
    text-align: left;
    max-width: 600px;
    margin-top: 2rem;
    padding: 0;
`;

const FeatureItem = styled.li`
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    color: #4a5568;
    animation: ${props => props.fadeOut ? fadeOut : fadeIn} 0.8s ease-in-out;
    text-align: center;
    line-height: 1.6;
    padding: 0 1rem;

    strong {
        color: #7f00ff;
        font-weight: 600;
    }
`;

const features = [
    "WhatsApp Integrado: Com nossa extensão para WhatsApp você poderá acompanhar e cadastrar leads diretamente pelo seu whatsapp web.",
    "CRM: Controle de Propostas: Cadastre leads através do Whatsapp com 1 clique. Converta oportunidades em clientes utilizando nossa ferramenta de funil de vendas.",
    "Base de Conhecimento: Concentre toda a sua base de conhecimento como script de vendas ou dúvidas em geral e disponibilize para seu time comercial.",
    "Departamento Societário: Controle os processos de abertura, troca, encerramento ou tarefas esporádicas.",
    "Clientes: Cadastro completo dos dados dos seus clientes. Importe ou exporte as informações com facilidade.",
    "Checklists: Cadastre tarefas que devem ser executadas toda vez que sejam realizadas: abertura de empresa, troca de contabilidade ou encerramento de empresa.",
    "Cadastro de Tarefas: Tarefas que se repetem mensalmente como DAS, Carnê Leão e E-SOCIAL. Tarefas que se repetem anualmente como 13º SALÁRIO, DIRF, TFE, TFA E TRSS. Ou tarefas esporádicas e customizadas.",
    "Relatórios: Gere relatórios detalhados sobre o desempenho da sua contabilidade.",
    "Integrações: Integre com outras ferramentas e plataformas para otimizar seu fluxo de trabalho.",
    "Suporte: Atendimento ao cliente para resolver suas dúvidas e problemas.",
    "Segurança: Proteja os dados da sua empresa com nossas medidas de segurança avançadas.",
    "Automação: Automatize processos repetitivos para ganhar eficiência.",
    "Customização: Personalize o sistema de acordo com as necessidades da sua empresa.",
    "Escalabilidade: Escale seu negócio com nossa plataforma robusta e flexível.",
    "Análises: Obtenha insights valiosos com nossas ferramentas de análise de dados."
];

const Loading = () => {
    const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
    const [fadeOut, setFadeOut] = useState(false);
    const [showLoadingText, setShowLoadingText] = useState(true);
    const [displayText, setDisplayText] = useState("");
    const [showFeatureList, setShowFeatureList] = useState(false);
    const [progress, setProgress] = useState(0);

    const fullText = "Enquanto estamos criando sua conta, acompanhe tudo que o Contlabs pode fazer pela sua contabilidade.";

    useEffect(() => {
        const interval = setInterval(() => {
            setFadeOut(true);
            setTimeout(() => {
                setCurrentFeatureIndex((prevIndex) => (prevIndex + 1) % features.length);
                setFadeOut(false);
            }, 800);
        }, 3000);

        let charIndex = 0;
        const typingInterval = setInterval(() => {
            if (charIndex < fullText.length) {
                setDisplayText(prev => prev + fullText[charIndex]);
                charIndex++;
                setProgress((charIndex / fullText.length) * 100);
            } else {
                clearInterval(typingInterval);
                setTimeout(() => {
                    setShowLoadingText(false);
                    setShowFeatureList(true);
                }, 1000);
            }
        }, 50);

        return () => {
            clearInterval(interval);
            clearInterval(typingInterval);
        };
    }, []);

    return (
        <LoadingWrapper>
            <LoadingSpinner />
            {showLoadingText && (
                <>
                    <TypingText>
                        {displayText}
                    </TypingText>
                </>
            )}
            {showFeatureList && (
                <FeatureList>
                    <FeatureItem fadeOut={fadeOut}>
                        <strong>{features[currentFeatureIndex].split(': ')[0]}:</strong> {features[currentFeatureIndex].split(': ')[1]}
                    </FeatureItem>
                </FeatureList>
            )}
        </LoadingWrapper>
    );
};

export default Loading;
